import { useEffect, useCallback, useImperativeHandle, forwardRef, useRef, useMemo } from 'react';
import { useParams } from 'react-router';
import { useAppSelector, selectPageData, selectPageScale, selectMetadata } from '@/store';
import { Page } from '@/api';
import { useGridNavigation } from '../hooks/useGridNavigation';
import { useGdprPageSelection } from '../hooks/useGdprPageSelection';
import { useGdprActions } from '../hooks/useGdprActions';
import { useUndoRedo } from '../hooks/useUndoRedo';
import { usePdfViewerLayout } from '../hooks/usePdfViewerLayout';
import { usePdfViewerVirtualization } from '../hooks/usePdfViewerVirtualization';
import { PdfPageGrid } from './PdfPageGrid';
import { useGdprControlsSetter } from '../GdprControlsContext';
import { useColumnSize } from '../ColumnSizeContext';

interface PdfViewerProps {
  documentId?: string;
}

export interface PdfViewerRef {
  scrollToPage: (pageIndex: number) => void;
}

const PdfViewer = forwardRef<PdfViewerRef, PdfViewerProps>((_props, ref) => {
  // Get document ID from URL params
  const { documentId } = useParams<{ documentId: string }>();

  // Redux state
  const pages = useAppSelector(selectPageData);
  const { columnSize } = useColumnSize();
  const storePageScale = useAppSelector(selectPageScale);
  const gdprDocMetadata = useAppSelector(selectMetadata);

  // Layout hook - handles container measurements and calculations
  const { parentRef, containerWidth, dynamicWidth, rowPageWidths, gapSize, rowHeights, totalRows } = usePdfViewerLayout(
    {
      pages,
      columnSize,
      storePageScale,
    },
  );

  // Virtualization hook - handles scrolling and virtualizer
  const {
    rowVirtualizer,
    scrollToPage: scrollToPageInternal,
    isScrolling,
  } = usePdfViewerVirtualization({
    totalRows,
    rowHeights,
    parentRef,
    containerWidth,
  });

  // Multi-select functionality
  const {
    selectedPageIds,
    handleSelectPage,
    isPageSelected: isPageSelectedFn,
    clearSelection,
    hasSelection,
  } = useGdprPageSelection(pages);

  // Track if we're in a multi-select operation to prevent interference
  const isMultiSelectRef = useRef(false);

  // Undo/Redo functionality for history tracking
  const { refetchHistory } = useUndoRedo({ documentId });

  // Grid navigation hook with proper handlers
  const {
    selectedIndex,
    setSelectedIndex,
    isSelected,
    handleKeyDown,
    sortedLabeledIndexes,
    findNextLabeledPage,
    findPreviousLabeledPage,
  } = useGridNavigation({
    totalItems: pages?.length || 0,
    columnCount: columnSize,
    initialSelection: 0, // Always start with first page
    pages: pages || undefined, // Pass pages array for label detection
    documentId, // Drives selectedIndex reset on doc change

    onSelectionChange: (index) => {
      // Scroll to the selected page row
      const rowIndex = Math.floor(index / columnSize);
      rowVirtualizer.scrollToIndex(rowIndex, { align: 'start' });

      // Only sync multi-selection with keyboard navigation if not in multi-select mode
      if (!isMultiSelectRef.current) {
        clearSelection(); // Clear existing selection
        handleSelectPage(
          {
            metaKey: false,
            ctrlKey: false,
            shiftKey: false,
          } as React.MouseEvent<HTMLDivElement>,
          index,
        );
      }
    },
    onMarkBreach: async () => {
      // Wrapper function that calls the main handler
      await handleMarkBreach();
    },
    onMarkBlank: async () => {
      // Wrapper function that calls the main handler
      await handleMarkBlank();
    },
    onClearLabels: async () => {
      // '3' key — mirrors the sidebar 'None' button
      await handleClearLabels();
    },
  });

  // GDPR Actions hook - now we have selectedIndex available
  const {
    handleMarkBreach,
    handleMarkBlank,
    handleClearLabels,
    isLoading: isGdprLoading,
  } = useGdprActions({
    documentId,
    hasSelection,
    selectedPageIds,
    pages,
    selectedIndex,
    clearSelection,
    refetchHistory, // Pass history refetch for undo/redo integration
  });

  // Page click handler
  const handlePageClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (e: React.MouseEvent<HTMLDivElement>, globalPageIndex: number, _pageItem: Page) => {
      // Set multi-select flag for shift/ctrl operations
      isMultiSelectRef.current = !!(e.shiftKey || e.ctrlKey || e.metaKey);

      // Handle multi-select with Shift+click, Ctrl+click
      handleSelectPage(e, globalPageIndex);

      // Only sync keyboard navigation for regular clicks (not multi-select)
      // This prevents interfering with multi-selection when using Shift/Ctrl
      if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
        setSelectedIndex(globalPageIndex);
      }

      // Reset multi-select flag after a short delay
      setTimeout(() => {
        isMultiSelectRef.current = false;
      }, 100);
    },
    [handleSelectPage, setSelectedIndex],
  );

  // Scroll to page function for external use
  const scrollToPage = useCallback(
    (pageIndex: number) => {
      if (!pages || pageIndex < 0 || pageIndex >= pages.length) return;
      scrollToPageInternal(pageIndex, columnSize);
      // Update the selected index to keep keyboard navigation in sync
      setSelectedIndex(pageIndex);
    },
    [pages, columnSize, scrollToPageInternal, setSelectedIndex],
  );

  // Expose methods via ref
  useImperativeHandle(
    ref,
    () => ({
      scrollToPage,
    }),
    [scrollToPage],
  );

  // Keep the focused page in view when the user changes the column count.
  // Without this, the scroll position (in pixels) stays fixed but the row
  // containing the focused page has moved, so a different page ends up under
  // the viewport. Defer one frame so the virtualizer has remeasured against
  // the new rowHeights produced by the new columnSize.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      scrollToPage(selectedIndex);
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSize]);

  // Publish GDPR controls state to the shared context so the Sidebar can render
  // the page input + Breach/Blank/Clear-Labels buttons + Prev/Next-labeled-page buttons
  // (which mirror the K/L keyboard shortcuts in useGridNavigation).
  const setGdprControls = useGdprControlsSetter();
  const totalPages = pages?.length || 0;
  const labeledPageCount = sortedLabeledIndexes.length;
  const hasLabeledPages = labeledPageCount > 0;
  // Review-progress metric — counts only pages the user has actively confirmed
  // (Breach or Blank), not pages the AI merely detected. Drives the sidebar progress bar.
  // Memoised: O(N) over pages, otherwise redone on every arrow press for 1000+ page docs.
  const confirmedLabeledPageCount = useMemo(
    () => pages?.reduce((acc, p) => acc + (p.confirmedBreach || p.confirmedBlank ? 1 : 0), 0) ?? 0,
    [pages],
  );
  // 1-based index of the focused page within the labeled set; null if the focused page has no label.
  const labeledPositionIndex = sortedLabeledIndexes.indexOf(selectedIndex);
  const currentLabeledPosition = labeledPositionIndex === -1 ? null : labeledPositionIndex + 1;

  // Source-document page numbers (mirrors what's rendered under each PDF page in PageView).
  const currentOriginalPageNumber = pages?.[selectedIndex]?.originalPageNumber ?? 0;
  // Memoised for the same reason as confirmedLabeledPageCount.
  const maxOriginalPageNumber = useMemo(
    () => pages?.reduce((max, p) => (p.originalPageNumber > max ? p.originalPageNumber : max), 0) ?? 0,
    [pages],
  );
  const goToOriginalPageNumber = useCallback(
    (originalPageNumber: number) => {
      if (!pages) return;
      const idx = pages.findIndex((p) => p.originalPageNumber === originalPageNumber);
      if (idx !== -1) scrollToPage(idx);
    },
    [pages, scrollToPage],
  );

  // Current page's confirmed label — drives the radio-selector visual in the Sidebar.
  // Detected-only labels intentionally map to 'none' (per design — only user-confirmed counts).
  const currentPageItem = pages?.[selectedIndex];
  const currentPageLabel: 'breach' | 'blank' | 'none' = currentPageItem?.confirmedBreach
    ? 'breach'
    : currentPageItem?.confirmedBlank
      ? 'blank'
      : 'none';

  const goPrevLabeledPage = useCallback(() => {
    if (!hasLabeledPages) return;
    const next = findPreviousLabeledPage(selectedIndex);
    if (next !== -1) scrollToPage(next);
  }, [hasLabeledPages, findPreviousLabeledPage, selectedIndex, scrollToPage]);

  const goNextLabeledPage = useCallback(() => {
    if (!hasLabeledPages) return;
    const next = findNextLabeledPage(selectedIndex);
    if (next !== -1) scrollToPage(next);
  }, [hasLabeledPages, findNextLabeledPage, selectedIndex, scrollToPage]);

  const gdprControls = useMemo(
    () => ({
      currentPage: selectedIndex + 1,
      totalPages,
      currentOriginalPageNumber,
      maxOriginalPageNumber,
      isLoading: isGdprLoading,
      hasLabeledPages,
      labeledPageCount,
      confirmedLabeledPageCount,
      currentLabeledPosition,
      currentPageLabel,
      onBreachClick: handleMarkBreach,
      onBlankClick: handleMarkBlank,
      onClearLabelsClick: handleClearLabels,
      onGoToPage: scrollToPage,
      onGoToOriginalPageNumber: goToOriginalPageNumber,
      onPrevLabeledPage: goPrevLabeledPage,
      onNextLabeledPage: goNextLabeledPage,
    }),
    [
      selectedIndex,
      totalPages,
      currentOriginalPageNumber,
      maxOriginalPageNumber,
      isGdprLoading,
      hasLabeledPages,
      labeledPageCount,
      confirmedLabeledPageCount,
      currentLabeledPosition,
      currentPageLabel,
      handleMarkBreach,
      handleMarkBlank,
      handleClearLabels,
      scrollToPage,
      goToOriginalPageNumber,
      goPrevLabeledPage,
      goNextLabeledPage,
    ],
  );

  useEffect(() => {
    setGdprControls(gdprControls);
  }, [gdprControls, setGdprControls]);

  // Focus container after it's fully rendered to enable keyboard navigation immediately
  useEffect(() => {
    const timer = setTimeout(() => {
      if (parentRef.current) {
        parentRef.current.focus({ preventScroll: true });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [containerWidth, parentRef]); // Focus when container is measured and ready

  // Global hotkey listener — lets the navigation/labelling shortcuts (arrows, K/L,
  // 9/0, 1/2/3) work regardless of which element currently has focus (sidebar,
  // actions panel, etc.). Skips when the user is typing in an input / textarea /
  // contenteditable so the page-jumper input and any modal text fields keep their
  // normal typing behavior. Also skips while a PrimeReact Dialog is open — its
  // body isn't an input, so without this check arrows/1-2-3 would silently move
  // or label pages behind the modal.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return;
        }
      }
      if (document.querySelector('.p-dialog-mask')) return;
      handleKeyDown(e as unknown as React.KeyboardEvent<HTMLDivElement>);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [handleKeyDown]);

  // Early returns
  if (!pages || pages.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-gray-500">No pages to display</div>
      </div>
    );
  }

  if (!containerWidth || rowHeights.length === 0) {
    return <div ref={parentRef} className="h-full w-full" />;
  }

  const rowItems = rowVirtualizer.getVirtualItems();

  // Main render
  return (
    <div className="relative h-full w-full">
      <div
        ref={parentRef}
        className="relative w-full max-w-full overflow-x-hidden overflow-y-auto focus:outline-none"
        style={{
          height: '90vh',
          width: '100%',
          overscrollBehavior: 'contain',
        }}
        tabIndex={0}
      >
        <div
          className="relative w-full"
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            marginBottom: '85px',
            marginTop: gdprDocMetadata && !gdprDocMetadata.aiChecked ? '30px' : '',
          }}
        >
          <PdfPageGrid
            rowItems={rowItems}
            pages={pages}
            columnSize={columnSize}
            gapSize={gapSize}
            dynamicWidth={dynamicWidth}
            rowPageWidths={rowPageWidths}
            storePageScale={storePageScale}
            isSelected={isSelected}
            isPageSelectedFn={isPageSelectedFn}
            onPageClick={handlePageClick}
            setSelectedIndex={setSelectedIndex}
            parentRef={parentRef}
            isScrolling={isScrolling}
          />
        </div>
      </div>
    </div>
  );
});

PdfViewer.displayName = 'PdfViewer';

export { PdfViewer };
